<template>
  <v-row>
    <v-col>
      <v-card class="elevation-0">
        <v-card-title>
          <span class="text-h5">Project Management</span>
        </v-card-title>

        <v-card-text>
          <v-tabs
            v-model="activeTab"
            @change="onTabChange"
          >
            <v-tab key="types">
              Project Types
            </v-tab>
            <v-tab key="statuses">
              Project Statuses
            </v-tab>
            <v-tab key="platforms">
              Project Platforms
            </v-tab>

            <!-- Project Types Tab -->
            <v-tab-item key="types">
              <v-card flat>
                <v-card-title>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    @click="showCreateDialog"
                  >
                    <v-icon left>
                      add
                    </v-icon>
                    Create Type
                  </v-btn>
                </v-card-title>

                <v-card-text>
                  <v-data-table
                    :headers="headers"
                    :items="projectTypes"
                    :loading="loadingTypes"
                    :search="searchTypes"
                    class="elevation-1"
                  >
                    <template v-slot:top>
                      <v-toolbar flat>
                        <v-text-field
                          v-model="searchTypes"
                          append-icon="search"
                          label="Search project types..."
                          single-line
                          hide-details
                        />
                      </v-toolbar>
                    </template>

                    <template v-slot:[`item.createdAt`]="{ item }">
                      {{ formatDate(item.createdAt) }}
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-icon
                        small
                        class="mr-2"
                        @click="editItem(item)"
                      >
                        edit
                      </v-icon>
                      <v-icon
                        small
                        @click="deleteItem(item)"
                      >
                        delete
                      </v-icon>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Project Statuses Tab -->
            <v-tab-item key="statuses">
              <v-card flat>
                <v-card-title>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    @click="showCreateDialog"
                  >
                    <v-icon left>
                      add
                    </v-icon>
                    Create Status
                  </v-btn>
                </v-card-title>

                <v-card-text>
                  <v-data-table
                    :headers="headers"
                    :items="projectStatuses"
                    :loading="loadingStatuses"
                    :search="searchStatuses"
                    class="elevation-1"
                  >
                    <template v-slot:top>
                      <v-toolbar flat>
                        <v-text-field
                          v-model="searchStatuses"
                          append-icon="search"
                          label="Search project statuses..."
                          single-line
                          hide-details
                        />
                      </v-toolbar>
                    </template>

                    <template v-slot:[`item.createdAt`]="{ item }">
                      {{ formatDate(item.createdAt) }}
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-icon
                        small
                        class="mr-2"
                        @click="editItem(item)"
                      >
                        edit
                      </v-icon>
                      <v-icon
                        small
                        @click="deleteItem(item)"
                      >
                        delete
                      </v-icon>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Project Platforms Tab -->
            <v-tab-item key="platforms">
              <v-card flat>
                <v-card-title>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    @click="showCreateDialog"
                  >
                    <v-icon left>
                      add
                    </v-icon>
                    Create Platform
                  </v-btn>
                </v-card-title>

                <v-card-text>
                  <v-data-table
                    :headers="headers"
                    :items="projectPlatforms"
                    :loading="loadingPlatforms"
                    :search="searchPlatforms"
                    class="elevation-1"
                  >
                    <template v-slot:top>
                      <v-toolbar flat>
                        <v-text-field
                          v-model="searchPlatforms"
                          append-icon="search"
                          label="Search project platforms..."
                          single-line
                          hide-details
                        />
                      </v-toolbar>
                    </template>

                    <template v-slot:[`item.createdAt`]="{ item }">
                      {{ formatDate(item.createdAt) }}
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-icon
                        small
                        class="mr-2"
                        @click="editItem(item)"
                      >
                        edit
                      </v-icon>
                      <v-icon
                        small
                        @click="deleteItem(item)"
                      >
                        delete
                      </v-icon>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Create/Edit Dialog -->
    <v-dialog
      v-model="dialog"
      max-width="500px"
      persistent
    >
      <v-card>
        <v-card-title>
          <v-col>
            <v-row>
              <v-col class="text-h6">
                <span>{{ formTitle }}</span>
              </v-col>

              <v-col class="text-right">
                <v-btn
                  icon
                  @click="close"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-card-title>

        <v-card-text v-if="editedItem">
          <v-form
            ref="form"
            v-model="valid"
          >
            <v-text-field
              v-model="editedItem.title"
              label="Title"
              :rules="titleRules"
              hint="Display name"
              required
              outlined
            />

            <!-- Value field is disabled if editing a project type to prevent issues updating the associated Firestore document. -->
            <v-text-field
              v-model="editedItem.value"
              label="Value"
              :rules="valueRules"
              hint="Unique identifier (e.g., 'consulting', 'advertising')"
              required
              :disabled="editedIndex !== -1"
              outlined
            />

            <v-textarea
              v-model="editedItem.description"
              label="Description (optional)"
              :rules="descriptionRules"
              auto-grow
              rows="15"
              counter="500"
              outlined
            />
          </v-form>
        </v-card-text>

        <v-card-text v-else>
          <!-- Show a message indicating no item is selected -->
          <span>No project type selected for editing.</span>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :loading="saving"
            :disabled="!valid"
            @click="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title class="headline">
          <span>Confirm Delete</span>
        </v-card-title>

        <v-card-text>
          <v-col v-if="itemToDelete">
            <span>Are you sure you want to delete the {{ currentItemType === 'type' ? 'project type' : currentItemType === 'status' ? 'project status' : 'project platform' }} "{{ itemToDelete.title || itemToDelete.value }}"?</span>
            <br><br>
            <strong>This action cannot be undone.</strong>
          </v-col>

          <v-col v-if="!itemToDelete">
            <span>No {{ currentItemType === 'type' ? 'project type' : currentItemType === 'status' ? 'project status' : 'project platform' }} selected for deletion.</span>
          </v-col>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="blue darken-1"
            text
            @click="closeDelete"
          >
            Cancel
          </v-btn>

          <v-btn
            color="red darken-1"
            text
            :loading="deleting"
            @click="deleteItemConfirm"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
export default {
  name: 'AdminProjects',
  data: () => ({
    activeTab: 0,
    dialog: false,
    deleteDialog: false,
    valid: false,
    searchTypes: '',
    searchStatuses: '',
    searchPlatforms: '',
    currentItemType: 'type', // 'type', 'status', or 'platform'
    headers: [
      {
        text: 'Value',
        align: 'start',
        sortable: true,
        value: 'value',
      },
      {
        text: 'Title',
        value: 'title',
        sortable: true,
      },
      {
        text: 'Description',
        value: 'description',
        sortable: false,
      },
      {
        text: 'Created At',
        value: 'createdAt',
        sortable: true,
      },
      {
        text: 'Created By',
        value: 'createdBy',
        sortable: true,
      },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false,
      },
    ],
    editedIndex: -1,
    editedItem: {
      value: '',
      title: '',
      description: '',
    },
    defaultItem: {
      value: '',
      title: '',
      description: '',
    },
    itemToDelete: null,
    originalValue: null, // Track original value for updates
    saving: false,
    deleting: false,
  }),

  computed: {
    projectTypes() {
      return this.$store.getters['admin/projectTypes']
    },
    loadingTypes() {
      return this.$store.getters['admin/isLoadingProjectTypes']
    },
    projectStatuses() {
      return this.$store.getters['admin/projectStatuses']
    },
    loadingStatuses() {
      return this.$store.getters['admin/isLoadingProjectStatuses']
    },
    projectPlatforms() {
      return this.$store.getters['admin/projectPlatforms']
    },
    loadingPlatforms() {
      return this.$store.getters['admin/isLoadingProjectPlatforms']
    },
    formTitle() {
      let itemType
      if (this.currentItemType === 'type') {
        itemType = 'Project Type'
      } else if (this.currentItemType === 'status') {
        itemType = 'Project Status'
      } else {
        itemType = 'Project Platform'
      }
      return this.editedIndex === -1 ? `New ${itemType}` : `Edit ${itemType}`
    },
    currentItems() {
      if (this.currentItemType === 'type') return this.projectTypes
      if (this.currentItemType === 'status') return this.projectStatuses
      return this.projectPlatforms
    },
    titleRules() {
      return [
        (v) => !!v || 'Title is required',
        (v) => /^[a-zA-Z0-9\s-_.()/»]+$/.test(v) || 'Limited to alphanumerics, spaces, hyphens, underscores, and parentheses',
        (v) => (v && v.length >= 2) || 'Title must be at least 2 characters',
        (v) => (v && v.length <= 30) || 'Title must be 30 characters or less',
        (v) => this.isValueUnique(v) || 'This title already exists',
      ]
    },
    valueRules() {
      return [
        (v) => !!v || 'Value is required',
        (v) => /^[a-zA-Z0-9\s-_.()]+$/.test(v) || 'Limited to alphanumerics, spaces, hyphens, underscores, and parentheses',
        (v) => (v && v.length >= 2) || 'Value must be at least 2 characters',
        (v) => (v && v.length <= 30) || 'Value must be 30 characters or less',
        (v) => this.isValueUnique(v) || 'This value already exists',
      ]
    },
    descriptionRules() {
      return [
        (v) => !v || v.length <= 500 || 'Description must be ≤ 500 characters',
      ]
    },
  },

  async created() {
    await Promise.all([
      this.$store.dispatch('admin/fetchProjectTypes', undefined, { root: true }),
      this.$store.dispatch('admin/fetchProjectStatuses', undefined, { root: true }),
      this.$store.dispatch('admin/fetchProjectPlatforms', undefined, { root: true }),
    ])
  },

  methods: {
    isValueUnique(value) {
      if (this.editedIndex !== -1) {
        // When editing, allow the current item's value
        const currentItem = this.currentItems[this.editedIndex]
        if (currentItem && currentItem.value === value) {
          return true
        }
      }
      return !this.currentItems.some((item) => item.value === value)
    },

    showCreateDialog() {
      this.editedIndex = -1
      this.editedItem = { ...this.defaultItem }
      this.originalValue = null
      this.dialog = true
    },

    editItem(item) {
      this.editedIndex = this.currentItems.indexOf(item)
      this.editedItem = { ...item }
      this.originalValue = item.value
      this.dialog = true
    },

    deleteItem(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    closeDelete() {
      this.deleteDialog = false
      this.itemToDelete = null
    },

    async deleteItemConfirm() {
      if (!this.itemToDelete) return

      this.deleting = true
      try {
        let action
        let itemTypeName

        if (this.currentItemType === 'type') {
          action = 'admin/deleteProjectType'
          itemTypeName = 'project type'
        } else if (this.currentItemType === 'status') {
          action = 'admin/deleteProjectStatus'
          itemTypeName = 'project status'
        } else {
          action = 'admin/deleteProjectPlatform'
          itemTypeName = 'project platform'
        }

        await this.$store.dispatch(action, this.itemToDelete.value)
        this.showSuccessMessage(`${itemTypeName.charAt(0).toUpperCase() + itemTypeName.slice(1)} "${this.itemToDelete.title || this.itemToDelete.value}" deleted successfully`)
      } catch (error) {
        let itemTypeName
        if (this.currentItemType === 'type') {
          itemTypeName = 'project type'
        } else if (this.currentItemType === 'status') {
          itemTypeName = 'project status'
        } else {
          itemTypeName = 'project platform'
        }
        this.showErrorMessage(`Failed to delete ${itemTypeName}`)
      } finally {
        this.deleting = false
        this.closeDelete()
      }
    },

    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
        this.originalValue = null
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },

    async save() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      try {
        const isType = this.currentItemType === 'type'
        const isStatus = this.currentItemType === 'status'

        let itemTypeName
        let action
        let payload

        if (isType) {
          itemTypeName = 'project type'
        } else if (isStatus) {
          itemTypeName = 'project status'
        } else {
          itemTypeName = 'project platform'
        }

        if (this.editedIndex === -1) {
          // Create new item
          if (isType) {
            action = 'admin/createProjectType'
          } else if (isStatus) {
            action = 'admin/createProjectStatus'
          } else {
            action = 'admin/createProjectPlatform'
          }

          const item = await this.$store.dispatch(action, this.editedItem)
          this.showSuccessMessage(`${itemTypeName.charAt(0).toUpperCase() + itemTypeName.slice(1)} "${item.title}" created successfully`)
        } else {
          // Update existing item
          if (isType) {
            action = 'admin/updateProjectType'
            payload = {
              projectType: this.editedItem,
              originalValue: this.originalValue,
            }
          } else if (isStatus) {
            action = 'admin/updateProjectStatus'
            payload = {
              projectStatus: this.editedItem,
              originalValue: this.originalValue,
            }
          } else {
            action = 'admin/updateProjectPlatform'
            payload = {
              projectPlatform: this.editedItem,
              originalValue: this.originalValue,
            }
          }

          const item = await this.$store.dispatch(action, payload)
          this.showSuccessMessage(`${itemTypeName.charAt(0).toUpperCase() + itemTypeName.slice(1)} "${item.title}" updated successfully`)
        }

        this.close()
      } catch (error) {
        let itemTypeName
        if (this.currentItemType === 'type') {
          itemTypeName = 'project type'
        } else if (this.currentItemType === 'status') {
          itemTypeName = 'project status'
        } else {
          itemTypeName = 'project platform'
        }
        this.showErrorMessage(`Failed to save ${itemTypeName}`)
      } finally {
        this.saving = false
      }
    },

    formatDate(date) {
      if (!date) return ''
      if (date.toDate) {
        // Firestore Timestamp
        return date.toDate().toLocaleDateString()
      }
      if (date instanceof Date) {
        return date.toLocaleDateString()
      }
      return new Date(date).toLocaleDateString()
    },

    showSuccessMessage(message) {
      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', {
        message,
        status: 'success',
      }, { root: true })
    },

    showErrorMessage(message) {
      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', {
        message,
        status: 'error',
      }, { root: true })
    },

    onTabChange() {
      // Update currentItemType based on activeTab
      if (this.activeTab === 0) {
        this.currentItemType = 'type'
      } else if (this.activeTab === 1) {
        this.currentItemType = 'status'
      } else {
        this.currentItemType = 'platform'
      }

      // Close any open dialogs when switching tabs
      this.dialog = false
      this.deleteDialog = false

      // Reset form state
      this.editedIndex = -1
      this.editedItem = { ...this.defaultItem }
      this.originalValue = null
      this.itemToDelete = null
    },
  },
}
</script>
